import { PDFDocument, rgb, type PDFFont, type PDFPage } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import {
    ASSISTANT_CONVERSATION_PDF_COLORS,
    ASSISTANT_CONVERSATION_PDF_PAGE,
    ASSISTANT_CONVERSATION_PDF_SPACING,
    ASSISTANT_CONVERSATION_PDF_TEXT_STYLES,
    createAssistantConversationPdfBlocks,
    type AssistantConversationPdfBlock,
} from "..";
import { getExportableConversation } from "./getExportableConversation";

type DownloadConversationPdfParams = {
    channelId: string;
    eventTitle: string;
    documentTitle?: string;
    emptyStateText?: string;
};

type TextBlockVariant = Extract<
    AssistantConversationPdfBlock,
    { type: "text" }
>["variant"];

type FontSet = {
    regular: PDFFont;
    bold: PDFFont;
};

type TypographyStyle = {
    font: keyof FontSet;
    fontSize: number;
    lineHeight: number;
    color: { red: number; green: number; blue: number };
    marginTop: number;
    marginBottom: number;
};

const ASSISTANT_CONVERSATION_PDF_FILENAME = "assistant-conversation.pdf";
const ASSISTANT_CONVERSATION_REGULAR_FONT_PATH = "/fonts/Roboto-Regular.ttf";
const ASSISTANT_CONVERSATION_BOLD_FONT_PATH = "/fonts/Roboto-Bold.ttf";
const DIVIDER_THICKNESS = 1;
const EMOJI_REGEX =
    /(?:[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF]\uFE0F?|\uFE0F)/g;

const removeEmoji = (value: string): string => value.replace(EMOJI_REGEX, "");

const loadFontBytes = async (path: string): Promise<ArrayBuffer> => {
    const response = await fetch(path);

    if (!response.ok) {
        throw new Error("Unable to load assistant conversation export font.");
    }

    return response.arrayBuffer();
};

function hexToRgb(value: string) {
    const normalized = value.replace("#", "");
    const expanded =
        normalized.length === 3
            ? normalized
                  .split("")
                  .map((char) => `${char}${char}`)
                  .join("")
            : normalized;

    const red = parseInt(expanded.slice(0, 2), 16) / 255;
    const green = parseInt(expanded.slice(2, 4), 16) / 255;
    const blue = parseInt(expanded.slice(4, 6), 16) / 255;

    return { red, green, blue };
}

const DIVIDER_COLOR = hexToRgb(ASSISTANT_CONVERSATION_PDF_COLORS.divider);

const TEXT_VARIANT_STYLES: Record<TextBlockVariant, TypographyStyle> = {
    title: {
        font: "bold",
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.title.fontSize,
        lineHeight:
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.title.fontSize *
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.title.lineHeight,
        color: hexToRgb(ASSISTANT_CONVERSATION_PDF_COLORS.title),
        marginTop: 0,
        marginBottom: 0,
    },
    eventTitle: {
        font: "bold",
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.eventTitle.fontSize,
        lineHeight:
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.eventTitle.fontSize *
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.eventTitle.lineHeight,
        color: hexToRgb(ASSISTANT_CONVERSATION_PDF_COLORS.subtitle),
        marginTop: ASSISTANT_CONVERSATION_PDF_SPACING.eventTitleTopMargin,
        marginBottom: 0,
    },
    role: {
        font: "bold",
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.role.fontSize,
        lineHeight:
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.role.fontSize *
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.role.lineHeight,
        color: hexToRgb(ASSISTANT_CONVERSATION_PDF_COLORS.role),
        marginTop: 0,
        marginBottom: ASSISTANT_CONVERSATION_PDF_SPACING.roleBottomMargin,
    },
    body: {
        font: "regular",
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.body.fontSize,
        lineHeight:
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.body.fontSize *
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.body.lineHeight,
        color: hexToRgb(ASSISTANT_CONVERSATION_PDF_COLORS.body),
        marginTop: 0,
        marginBottom: 0,
    },
    emptyState: {
        font: "regular",
        fontSize: ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.emptyState.fontSize,
        lineHeight:
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.emptyState.fontSize *
            ASSISTANT_CONVERSATION_PDF_TEXT_STYLES.emptyState.lineHeight,
        color: hexToRgb(ASSISTANT_CONVERSATION_PDF_COLORS.body),
        marginTop: 0,
        marginBottom: 0,
    },
};

const createDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
};

const getContentWidth = (): number =>
    ASSISTANT_CONVERSATION_PDF_PAGE.width -
    ASSISTANT_CONVERSATION_PDF_PAGE.paddingLeft -
    ASSISTANT_CONVERSATION_PDF_PAGE.paddingRight;

const getBottomLimit = (): number =>
    ASSISTANT_CONVERSATION_PDF_PAGE.paddingBottom;

const createPageState = (pdf: PDFDocument) => {
    const page = pdf.addPage([
        ASSISTANT_CONVERSATION_PDF_PAGE.width,
        ASSISTANT_CONVERSATION_PDF_PAGE.height,
    ]);

    return {
        page,
        cursorY:
            ASSISTANT_CONVERSATION_PDF_PAGE.height -
            ASSISTANT_CONVERSATION_PDF_PAGE.paddingTop,
    };
};

const splitLongWord = (
    word: string,
    font: PDFFont,
    fontSize: number,
    maxWidth: number
): string[] => {
    const parts: string[] = [];
    let current = "";

    for (const character of word) {
        const next = `${current}${character}`;
        if (current && font.widthOfTextAtSize(next, fontSize) > maxWidth) {
            parts.push(current);
            current = character;
        } else {
            current = next;
        }
    }

    if (current) {
        parts.push(current);
    }

    return parts;
};

const wrapParagraph = (
    paragraph: string,
    font: PDFFont,
    fontSize: number,
    maxWidth: number
): string[] => {
    if (!paragraph) {
        return [""];
    }

    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return [""];
    }

    const lines: string[] = [];
    let currentLine = "";

    words.forEach((word) => {
        const segments =
            font.widthOfTextAtSize(word, fontSize) > maxWidth
                ? splitLongWord(word, font, fontSize, maxWidth)
                : [word];

        segments.forEach((segment) => {
            const candidate = currentLine
                ? `${currentLine} ${segment}`
                : segment;

            if (
                currentLine &&
                font.widthOfTextAtSize(candidate, fontSize) > maxWidth
            ) {
                lines.push(currentLine);
                currentLine = segment;
                return;
            }

            if (
                !currentLine &&
                font.widthOfTextAtSize(candidate, fontSize) > maxWidth
            ) {
                lines.push(candidate);
                return;
            }

            currentLine = candidate;
        });
    });

    if (currentLine) {
        lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [""];
};

const wrapText = (
    text: string,
    font: PDFFont,
    fontSize: number,
    maxWidth: number
): string[] =>
    text
        .split("\n")
        .reduce<string[]>(
            (allLines, paragraph: string) => [
                ...allLines,
                ...wrapParagraph(paragraph, font, fontSize, maxWidth),
            ],
            []
        );

const ensureSpaceForHeight = (
    pageState: { page: PDFPage; cursorY: number },
    pdf: PDFDocument,
    height: number
) => {
    if (pageState.cursorY - height >= getBottomLimit()) {
        return pageState;
    }

    return createPageState(pdf);
};

const drawDivider = (
    pageState: { page: PDFPage; cursorY: number },
    pdf: PDFDocument
) => {
    const margin = ASSISTANT_CONVERSATION_PDF_SPACING.sectionDividerMargin;
    let nextState = ensureSpaceForHeight(
        pageState,
        pdf,
        margin * 2 + DIVIDER_THICKNESS
    );

    nextState.cursorY -= margin;
    nextState.page.drawLine({
        start: {
            x: ASSISTANT_CONVERSATION_PDF_PAGE.paddingLeft,
            y: nextState.cursorY,
        },
        end: {
            x:
                ASSISTANT_CONVERSATION_PDF_PAGE.width -
                ASSISTANT_CONVERSATION_PDF_PAGE.paddingRight,
            y: nextState.cursorY,
        },
        thickness: DIVIDER_THICKNESS,
        color: rgb(DIVIDER_COLOR.red, DIVIDER_COLOR.green, DIVIDER_COLOR.blue),
    });
    nextState.cursorY -= margin;

    return nextState;
};

const drawTextBlock = (
    block: Extract<AssistantConversationPdfBlock, { type: "text" }>,
    pageState: { page: PDFPage; cursorY: number },
    pdf: PDFDocument,
    fonts: FontSet
) => {
    const style = TEXT_VARIANT_STYLES[block.variant];
    const font = fonts[style.font];
    const lines = wrapText(
        removeEmoji(block.text),
        font,
        style.fontSize,
        getContentWidth()
    );

    let nextState = pageState;
    if (style.marginTop > 0) {
        nextState = ensureSpaceForHeight(nextState, pdf, style.marginTop);
        nextState.cursorY -= style.marginTop;
    }

    lines.forEach((line) => {
        nextState = ensureSpaceForHeight(nextState, pdf, style.lineHeight);
        nextState.page.drawText(line, {
            x: ASSISTANT_CONVERSATION_PDF_PAGE.paddingLeft,
            y: nextState.cursorY - style.fontSize,
            size: style.fontSize,
            font,
            color: rgb(style.color.red, style.color.green, style.color.blue),
        });
        nextState.cursorY -= style.lineHeight;
    });

    if (style.marginBottom > 0) {
        nextState = ensureSpaceForHeight(nextState, pdf, style.marginBottom);
        nextState.cursorY -= style.marginBottom;
    }

    return nextState;
};

export const downloadConversationPdf = async ({
    channelId,
    eventTitle,
    documentTitle,
    emptyStateText,
}: DownloadConversationPdfParams): Promise<void> => {
    const normalizedChannelId = channelId.trim();
    if (!normalizedChannelId) {
        throw new Error("Assistant conversation export requires a channelId.");
    }

    if (typeof window === "undefined" || typeof document === "undefined") {
        throw new Error("Assistant conversation export requires browser APIs.");
    }

    const messages = getExportableConversation(normalizedChannelId);
    const blocks = createAssistantConversationPdfBlocks({
        eventTitle,
        messages,
        documentTitle,
        emptyStateText,
    });

    try {
        const pdf = await PDFDocument.create();
        pdf.registerFontkit(fontkit);
        const [regularFontBytes, boldFontBytes] = await Promise.all([
            loadFontBytes(ASSISTANT_CONVERSATION_REGULAR_FONT_PATH),
            loadFontBytes(ASSISTANT_CONVERSATION_BOLD_FONT_PATH),
        ]);
        const fonts: FontSet = {
            regular: await pdf.embedFont(regularFontBytes),
            bold: await pdf.embedFont(boldFontBytes),
        };

        let pageState = createPageState(pdf);

        blocks.forEach((block) => {
            pageState =
                block.type === "divider"
                    ? drawDivider(pageState, pdf)
                    : drawTextBlock(block, pageState, pdf, fonts);
        });

        const pdfBytes = await pdf.save();
        const pdfArrayBuffer = pdfBytes.buffer.slice(
            pdfBytes.byteOffset,
            pdfBytes.byteOffset + pdfBytes.byteLength
        ) as ArrayBuffer;
        const blob = new Blob([pdfArrayBuffer], {
            type: "application/pdf",
        });
        createDownload(blob, ASSISTANT_CONVERSATION_PDF_FILENAME);
    } catch (error) {
        throw new Error(
            error instanceof Error
                ? error.message
                : "Assistant conversation export failed."
        );
    }
};
